import { Controller } from '@hotwired/stimulus';
import DataTable from 'datatables.net-dt';

export default class extends Controller {
    static targets = ['tableDom'];
    static values = {
        remember: { type: Boolean, default: true },
    };

    table = null;
    storageKey = null;

    clearHighlights() {
        if (!this.hasTableDomTarget) {
            return;
        }

        const highlights = this.tableDomTarget.querySelectorAll('span.highlight');
        highlights.forEach((span) => {
            span.replaceWith(document.createTextNode(span.textContent || ''));
        });
    }

    applyHighlights() {
        if (!this.hasTableDomTarget) {
            return;
        }

        this.clearHighlights();

        const textFilters = this.tableDomTarget.querySelectorAll(
            'tfoot .filter input[data-type="texte"]',
        );

        textFilters.forEach((input) => {
            const value = (input.value || '').trim();
            const index = Number(input.getAttribute('data-index'));

            if (value === '' || Number.isNaN(index)) {
                return;
            }

            const escaped = value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
            const regex = new RegExp(`(${escaped})`, 'gi');
            const rows = this.tableDomTarget.querySelectorAll('tbody tr');

            rows.forEach((row) => {
                const cell = row.querySelector(`td:nth-child(${index + 1})`);
                if (!cell) {
                    return;
                }

                const text = cell.textContent || '';
                if (text.trim() === '') {
                    return;
                }

                const highlighted = text.replace(regex, '<span class="highlight">$1</span>');
                if (highlighted !== text) {
                    cell.innerHTML = highlighted;
                }
            });
        });
    }

    getStorageKey() {
        if (this.storageKey) {
            return this.storageKey;
        }

        const path = window.location.pathname || 'unknown';
        const tableId = this.tableDomTarget?.id ? `#${this.tableDomTarget.id}` : '';
        this.storageKey = `datatableState:${path}${tableId}`;

        return this.storageKey;
    }

    collectFilterState() {
        const filtersState = {};
        const inputs = document.querySelectorAll('tr.filters .filter input, tr.filters .filter select');

        inputs.forEach((input) => {
            const index = input.getAttribute('data-index') || 'none';
            const type = input.getAttribute('data-type') || 'value';
            const key = `${index}:${type}`;
            filtersState[key] = input.value;
        });

        return filtersState;
    }

    applyFilterState(filtersState) {
        if (!filtersState || !this.table) {
            return;
        }

        const inputs = document.querySelectorAll('tr.filters .filter input, tr.filters .filter select');

        inputs.forEach((input) => {
            const index = input.getAttribute('data-index') || 'none';
            const type = input.getAttribute('data-type') || 'value';
            const key = `${index}:${type}`;

            if (!(key in filtersState)) {
                return;
            }

            const savedValue = filtersState[key];
            if (savedValue === undefined || savedValue === null || savedValue === '') {
                return;
            }

            input.value = savedValue;
            this.updateTable({ target: input });
        });
    }

    loadTableState() {
        try {
            const key = this.getStorageKey();
            if (!key || !window.sessionStorage) {
                return null;
            }

            const raw = sessionStorage.getItem(key);
            if (!raw) {
                return null;
            }

            return JSON.parse(raw);
        } catch (e) {
            return null;
        }
    }

    saveTableState() {
        if (!this.table) {
            return;
        }

        try {
            const key = this.getStorageKey();
            if (!key || !window.sessionStorage) {
                return;
            }

            const info = this.table.page.info();
            const state = {
                page: info ? info.page : 0,
                length: info ? info.length : null,
                filters: this.collectFilterState(),
            };

            sessionStorage.setItem(key, JSON.stringify(state));
        } catch (e) {
            // noop
        }
    }

    restoreTableState() {
        const state = this.loadTableState();
        if (!state || !this.table) {
            return;
        }

        if (state.filters) {
            this.applyFilterState(state.filters);
        }

        const info = this.table.page.info();
        const currentLength = info ? info.length : null;

        if (state.length && state.length !== currentLength) {
            this.table.page.len(state.length);
        }

        if (typeof state.page === 'number') {
            this.table.page(state.page);
        }

        this.table.draw(false);
    }

    connect() {
        if (!this.hasTableDomTarget || this.tableDomTarget.classList.contains('dataTable')) {
            return;
        }

        const allNotSortable = document.querySelectorAll('.notSort');
        const notSortableIndexes = [];
        allNotSortable.forEach((th) => {
            notSortableIndexes.push(parseInt(th.getAttribute('data-column') || '0', 10));
        });

        this.table = new DataTable(this.tableDomTarget, {
            responsive: true,
            pagingType: 'simple_numbers',
            pageLength: 10,
            dom: 'rtip',
            order: [],
            language: {
                processing: 'Traitement en cours...',
                search: 'Rechercher ',
                lengthMenu: 'Afficher _MENU_ éléments',
                info: "Affichage de l'élément _START_ à _END_ sur _TOTAL_ éléments",
                infoEmpty: "Affichage de l'élément 0 à 0 sur 0 élément",
                infoFiltered: '(filtré de _MAX_ éléments au total)',
                infoPostFix: '',
                loadingRecords: 'Chargement en cours...',
                zeroRecords: 'Aucun élément correspondant trouvé',
                emptyTable: 'Aucune donnée disponible dans le tableau',
                paginate: {
                    first: 'Premier',
                    previous: 'Précédent',
                    next: 'Suivant',
                    last: 'Dernier',
                },
                aria: {
                    sortAscending: ': activer pour trier la colonne par ordre croissant',
                    sortDescending: ': activer pour trier la colonne par ordre décroissant',
                },
            },
            columnDefs: [
                {
                    targets: notSortableIndexes,
                    orderable: false,
                },
            ],
        });

        if (this.rememberValue) {
            this.restoreTableState();
            this.table.on('page.dt draw.dt search.dt order.dt', () => {
                this.saveTableState();
            });
        }

        this.table.on('draw.dt search.dt', () => {
            this.applyHighlights();
        });

        this.applyHighlights();
    }

    disconnect() {
        if (this.table) {
            this.table.destroy();
            this.table = null;
        }
    }

    updateTable(event) {
        const index = Number(event.target.getAttribute('data-index'));
        const type = event.target.getAttribute('data-type');

        if (Number.isNaN(index)) {
            return;
        }

        if (type === 'min-range' || type === 'max-range' || type === 'min-duration' || type === 'max-duration') {
            const container = event.target.closest('td');
            if (!container) {
                return;
            }

            const isDurationRange = type === 'min-duration' || type === 'max-duration';
            const minInput = container.querySelector(
                isDurationRange ? 'input[data-type="min-duration"]' : 'input[data-type="min-range"]',
            );
            const maxInput = container.querySelector(
                isDurationRange ? 'input[data-type="max-duration"]' : 'input[data-type="max-range"]',
            );
            if (!minInput || !maxInput) {
                return;
            }

            this.table.column(index).search('', false, false).draw();
            this.table.column(index).search.fixed('range', (searchStr, data) => {
                const rawValue = data[index] || '';

                if (isDurationRange) {
                    const min = this.parseDurationToMinutes(minInput.value);
                    const max = this.parseDurationToMinutes(maxInput.value);
                    const value = this.parseDurationToMinutes(rawValue);

                    return (
                        (Number.isNaN(min) && Number.isNaN(max)) ||
                        (Number.isNaN(min) && value <= max) ||
                        (min <= value && Number.isNaN(max)) ||
                        (min <= value && value <= max)
                    );
                }

                if (container.classList.contains('input-number-container')) {
                    const min = parseFloat(minInput.value);
                    const max = parseFloat(maxInput.value);
                    const value = parseFloat(rawValue.replace(',', '.'));

                    return (
                        (Number.isNaN(min) && Number.isNaN(max)) ||
                        (Number.isNaN(min) && value <= max) ||
                        (min <= value && Number.isNaN(max)) ||
                        (min <= value && value <= max)
                    );
                }

                return true;
            });

            this.table.draw();
        } else {
            const value = event.target.value;
            this.table.column(index).search(value).draw();
        }
    }

    parseDurationToMinutes(raw) {
        const input = String(raw || '').trim().toLowerCase();
        if (input === '') {
            return Number.NaN;
        }

        const compact = input.replace(/\s+/g, '');
        const hoursOnly = compact.match(/^(\d+)$/);
        if (hoursOnly) {
            return Number(hoursOnly[1]) * 60;
        }

        const hFormat = compact.match(/^(\d+)h([0-5]?\d)?$/);
        if (hFormat) {
            const hours = Number(hFormat[1] || '0');
            const minutes = Number(hFormat[2] || '0');
            return (hours * 60) + minutes;
        }

        const colonFormat = compact.match(/^(\d+):([0-5]?\d)$/);
        if (colonFormat) {
            const hours = Number(colonFormat[1] || '0');
            const minutes = Number(colonFormat[2] || '0');
            return (hours * 60) + minutes;
        }

        return Number.NaN;
    }
}
